import { useCallback } from 'react';
import { FieldError, FieldErrors, Resolver } from 'react-hook-form';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { globalAttributes } from '../../common/global-attributes';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventPanelMetadataStore } from '../event-state/event-panel-metadata-store';
import { EventPanelFormType } from '@mark43/cad-api';
import { isInteger, toNumber } from 'lodash';
import {
    EVENT_MAXIMUM_NUMBER_OF_LABELS,
    LABELS_VALIDATION_ERROR_MESSAGE,
} from '../../common/constants';
import {
    isValidVin,
    validateEntityPanelVehicles,
} from '../../panels/entity-panels/forms/vehicles/validate-entity-panel-vehicles';
import { validateEntityPanelPersonsForm } from '../../panels/entity-panels/forms/persons/validate-entity-panel-persons';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { validationError } from '../../panels/entity-panels/forms/validation';

/* eslint-disable no-lone-blocks */
// we disable no lone blocks because the blocks are sometimes practical and other times
// convenient as a visual aid in the viewing of rules relating to themes
export function useEventPanelFormValidationResolver(
    eventMetadataStore: EventPanelMetadataStore
): Resolver<EventPanelFormType> {
    const { isFirstResponder, isCad } = useApplicationType();
    const eventPanelConfiguration = useBootstrapState().eventPanelConfiguration;
    const eventPanelOptions = useBootstrapState().eventPanelOptions;
    const enabledFeatures = useEnabledFeatures();
    return useCallback(
        async (form) => {
            const eventDoesNotExist = !eventMetadataStore.state.metadata?.eventId;
            const errors: FieldErrors<EventPanelFormType> = {};
            const userIsAttemptingTransmit =
                typeof form.eventCommunication != null &&
                form.eventCommunication?.value ===
                    eventPanelConfiguration?.eventCommunicationEmergency.value;
            if (!form.location) {
                errors.location = validationError('Required');
            }
            {
                // police fields
                const cfsIncludesPolice = form.callForService?.agencyTypeGlobalAttrIds.includes(
                    globalAttributes.agencyTypeGlobal.POLICE
                );
                if (cfsIncludesPolice && !form.policeDispatchArea) {
                    errors.policeDispatchArea = validationError('Required');
                }
                if (cfsIncludesPolice && !form.policeAgency) {
                    errors.policeAgency = validationError('Required');
                }
            }
            {
                // fire fields
                const cfsIncludesFire = form.callForService?.agencyTypeGlobalAttrIds.includes(
                    globalAttributes.agencyTypeGlobal.FIRE
                );
                if (cfsIncludesFire && !form.fireDispatchArea) {
                    errors.fireDispatchArea = validationError('Required');
                }
                if (cfsIncludesFire && !form.fireAgency) {
                    errors.fireAgency = validationError('Required');
                }
            }
            {
                // ems fields
                const cfsIncludesEms = form.callForService?.agencyTypeGlobalAttrIds.includes(
                    globalAttributes.agencyTypeGlobal.EMS
                );
                if (cfsIncludesEms && !form.emsDispatchArea) {
                    errors.emsDispatchArea = validationError('Required');
                }
                if (cfsIncludesEms && !form.emsAgency) {
                    errors.emsAgency = validationError('Required');
                }
            }
            {
                // cfs fields
                const callForService = form.callForService;
                if (!callForService) {
                    errors.callForService = validationError('Required');
                }

                if (callForService) {
                    if (
                        form.additionalCallForServices.filter(
                            (additionalCfs) => additionalCfs.value === callForService.value
                        ).length > 0
                    ) {
                        errors.additionalCallForServices = validationError(
                            'Cannot add additional CFS that is the same as the primary CFS'
                        );
                    }
                    if (
                        callForService.agencyTypeGlobalAttrIds.length > 1 &&
                        form.additionalCallForServices.length > 0 &&
                        !errors.additionalCallForServices
                    ) {
                        errors.additionalCallForServices = validationError(
                            'Cannot add additional CFS when the primary CFS is combined'
                        );
                    }
                    if (
                        form.additionalCallForServices.find(
                            (additionalCfs) =>
                                additionalCfs.agencyTypeGlobalAttrIds[0] !==
                                callForService.agencyTypeGlobalAttrIds[0]
                        ) &&
                        !errors.additionalCallForServices
                    ) {
                        errors.additionalCallForServices = validationError(
                            'All additional CFS must have the same class as the primary CFS'
                        );
                    }
                    if (
                        form.eventLabels &&
                        form.eventLabels.length > EVENT_MAXIMUM_NUMBER_OF_LABELS
                    ) {
                        errors.eventLabels = validationError(LABELS_VALIDATION_ERROR_MESSAGE);
                    }
                }
                if (form.additionalCallForServices.length > 5) {
                    errors.additionalCallForServices = validationError(
                        'Cannot add more than 5 additional CFS'
                    );
                }
            }
            {
                if (!form.riskLevel && enabledFeatures.eventsHaveRiskLevel) {
                    errors.riskLevel = validationError('Required');
                }
                if (!form.priority) {
                    errors.priority = validationError('Required');
                }
                if (!form.eventOrigin) {
                    errors.eventOrigin = validationError('Required');
                }
                if (!form.eventQueue) {
                    errors.eventQueue = validationError('Required');
                }
            }
            {
                // narrative
                if (
                    !isFirstResponder &&
                    !userIsAttemptingTransmit &&
                    !form.narrative &&
                    eventDoesNotExist
                ) {
                    errors.narrative = validationError('Required');
                }
            }
            {
                // persons / reporting parties / involved persons
                const personValidationArgs = {
                    form,
                    isCad,
                    enabledFeatures,
                    eventPanelOptions,
                    userIsAttemptingTransmit,
                    existingErrors: errors,
                    entityExists: !eventDoesNotExist,
                };
                validateEntityPanelPersonsForm({
                    ...personValidationArgs,
                    baseKey: 'reportingParties',
                });
                validateEntityPanelPersonsForm({
                    ...personValidationArgs,
                    baseKey: 'involvedPersons',
                });
            }
            {
                // vehicles
                validateEntityPanelVehicles({
                    form: { ...form, involvedVehicles: [] },
                    baseKey: 'vehicles',
                    eventPanelOptions,
                    existingErrors: errors,
                    departmentDexRegion: eventPanelConfiguration.departmentDexRegion,
                });
            }
            {
                // tow vehicles
                form.towVehicles.forEach((towVehicle, index) => {
                    const localErrors: Partial<
                        Record<keyof EventPanelFormType['towVehicles'][number], FieldError>
                    > = {};
                    if (!isValidVin(towVehicle.vinNumber)) {
                        localErrors.vinNumber = validationError('Please enter a valid VIN');
                    }
                    if (!towVehicle.towTypeOption) {
                        localErrors.towTypeOption = validationError('Required');
                    }
                    if (!towVehicle.towedFromLocation) {
                        localErrors.towedFromLocation = validationError('Required');
                    }
                    if (
                        !towVehicle.towedToLocation &&
                        towVehicle.towTypeOption?.value !== 'Private'
                    ) {
                        localErrors.towedToLocation = validationError('Required');
                    }

                    if (
                        towVehicle.year !== null &&
                        towVehicle.year !== undefined &&
                        !isInteger(toNumber(towVehicle.year))
                    ) {
                        localErrors.year = validationError('Must be a whole number');
                    }
                    if (Object.keys(localErrors).length > 0) {
                        if (!errors.towVehicles) {
                            errors.towVehicles = [];
                        }
                        if (!errors.towVehicles[index]) {
                            errors.towVehicles[index] = localErrors;
                        }
                    }
                });
            }
            {
                // clear event fields
                if (form.clearEventComments && form.clearEventComments.length > 1024) {
                    errors.clearEventComments = validationError('Must be under 1024 characters');
                }
            }

            if (Object.keys(errors).length > 0) {
                return {
                    values: {},
                    errors,
                };
            } else {
                return {
                    values: form,
                    errors: {},
                };
            }
        },
        [
            eventMetadataStore.state.metadata?.eventId,
            eventPanelConfiguration?.eventCommunicationEmergency.value,
            eventPanelConfiguration.departmentDexRegion,
            enabledFeatures,
            isFirstResponder,
            eventPanelOptions,
            isCad,
        ]
    );
}
